import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import Glyph from '../glyphs/Glyph';

interface TicTacToeGameProps {
  onComplete: (score: number) => void;
}

type Player = 'X' | 'O' | null;
type Difficulty = 'EASY' | 'MEDIUM' | 'HARD';

export default function TicTacToe({ onComplete }: TicTacToeGameProps) {
  const [board, setBoard] = useState<Player[]>(Array(9).fill(null));
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);
  const [winner, setWinner] = useState<Player | 'DRAW' | null>(null);
  const [difficulty, setDifficulty] = useState<Difficulty | null>(null);

  const checkWinner = (squares: Player[]) => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8], // Rows
      [0, 3, 6], [1, 4, 7], [2, 5, 8], // Cols
      [0, 4, 8], [2, 4, 6]             // Diagonals
    ];

    for (const [a, b, c] of lines) {
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return squares[a];
      }
    }
    return squares.every(s => s !== null) ? 'DRAW' : null;
  };

  useEffect(() => {
    if (!isPlayerTurn && !winner) {
      // Computer move delay
      const timeout = setTimeout(makeComputerMove, 600);
      return () => clearTimeout(timeout);
    }
    // Only trigger on turn change; makeComputerMove reads the current board from this render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlayerTurn, winner]);

  useEffect(() => {
    if (winner) {
      if (winner === 'X') {
        const points = difficulty === 'HARD' ? 30 : difficulty === 'MEDIUM' ? 20 : 10;
        setTimeout(() => onComplete(points), 1000);
      } else if (winner === 'DRAW') {
        setTimeout(() => onComplete(5), 1000);
      }
    }
    // Report the result exactly once per game end.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [winner]);

  const makeComputerMove = () => {
    let moveIndex: number;

    if (difficulty === 'EASY') {
      moveIndex = getRandomMove(board);
    } else if (difficulty === 'MEDIUM') {
      // 50% chance of best move, 50% random
      moveIndex = Math.random() > 0.5 ? getBestMove(board) : getRandomMove(board);
    } else {
      moveIndex = getBestMove(board);
    }

    if (moveIndex !== -1) {
      const newBoard = [...board];
      newBoard[moveIndex] = 'O';
      setBoard(newBoard);
      const gameResult = checkWinner(newBoard);
      setWinner(gameResult);
      setIsPlayerTurn(true);
    }
  };

  const getRandomMove = (squares: Player[]) => {
    const available = squares.map((v, i) => v === null ? i : -1).filter(i => i !== -1);
    return available[Math.floor(Math.random() * available.length)];
  };

  const getBestMove = (squares: Player[]) => {
    // Simple Minimax for 3x3
    let bestScore = -Infinity;
    let move = -1;
    
    for (let i = 0; i < 9; i++) {
        if (squares[i] === null) {
            squares[i] = 'O';
            const score = minimax(squares, 0, false);
            squares[i] = null;
            if (score > bestScore) {
                bestScore = score;
                move = i;
            }
        }
    }
    return move !== -1 ? move : getRandomMove(squares);
  };

  const scores = { O: 1, X: -1, DRAW: 0 };

  const minimax = (squares: Player[], depth: number, isMaximizing: boolean) => {
      const result = checkWinner(squares);
      if (result !== null) {
          return scores[result as keyof typeof scores] || 0;
      }

      if (isMaximizing) {
          let bestScore = -Infinity;
          for (let i = 0; i < 9; i++) {
              if (squares[i] === null) {
                  squares[i] = 'O';
                  const score = minimax(squares, depth + 1, false);
                  squares[i] = null;
                  bestScore = Math.max(score, bestScore);
              }
          }
          return bestScore;
      } else {
          let bestScore = Infinity;
          for (let i = 0; i < 9; i++) {
              if (squares[i] === null) {
                  squares[i] = 'X';
                  const score = minimax(squares, depth + 1, true);
                  squares[i] = null;
                  bestScore = Math.min(score, bestScore);
              }
          }
          return bestScore;
      }
  };

  const handleClick = (i: number) => {
    if (board[i] || !isPlayerTurn || winner) return;

    const newBoard = [...board];
    newBoard[i] = 'X';
    setBoard(newBoard);
    
    const gameResult = checkWinner(newBoard);
    if (gameResult) {
      setWinner(gameResult);
    } else {
      setIsPlayerTurn(false);
    }
  };

  if (!difficulty) {
      return (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-foreground p-6">
              <h2 className="text-3xl font-bold mb-8">איקס עיגול</h2>
              <p className="mb-4 text-muted-foreground">בחר רמת קושי:</p>
              <button onClick={() => setDifficulty('EASY')} className="w-full max-w-xs p-4 bg-green-600 rounded-xl font-bold text-xl hover:bg-green-500">קל (10)</button>
              <button onClick={() => setDifficulty('MEDIUM')} className="w-full max-w-xs p-4 bg-yellow-600 rounded-xl font-bold text-xl hover:bg-yellow-500">בינוני (20)</button>
              <button onClick={() => setDifficulty('HARD')} className="w-full max-w-xs p-4 bg-red-600 rounded-xl font-bold text-xl hover:bg-red-500">בלתי אפשרי (30)</button>
          </div>
      );
  }

  return (
    <div className="flex flex-col items-center justify-center p-4 h-full text-foreground">
      <div className="flex justify-between w-full max-w-xs mb-6 items-center">
        <h2 className="text-2xl font-bold">איקס עיגול</h2>
        <button onClick={() => { setBoard(Array(9).fill(null)); setWinner(null); setIsPlayerTurn(true); }} className="p-2 bg-muted rounded-lg hover:bg-accent">
            <RefreshCw size={20} />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 bg-muted p-3 rounded-2xl">
        {board.map((cell, i) => (
          <button
            key={i}
            onClick={() => handleClick(i)}
            disabled={!!cell || !isPlayerTurn || !!winner}
            className="w-20 h-20 sm:w-24 sm:h-24 bg-muted rounded-xl flex items-center justify-center text-4xl disabled:opacity-100 hover:bg-accent transition-colors"
          >
            {cell === 'X' && <Glyph name="ex" size={48} />}
            {cell === 'O' && <Glyph name="ring" size={48} />}
          </button>
        ))}
      </div>

      {winner && (
        <div className="mt-8 text-2xl font-bold animate-bounce">
            {winner === 'X' ? 'ניצחת!' : winner === 'O' ? 'הפסדת!' : 'תיקו!'}
        </div>
      )}
      
      {!winner && (
          <div className="mt-8 text-lg text-muted-foreground">
              {isPlayerTurn ? 'תורך (X)' : 'מחשב חושב...'}
          </div>
      )}
    </div>
  );
}
